import * as React from "react";
import {connect} from "react-redux";

import {useApolloGqlClient} from "@khan/data-gql-apollo";

import type {
    WithApolloProps,
    WithoutApollo,
} from "#data-access/gql/with-apollo.tsx";
import {Provider} from "#data-access/react-redux-provider.tsx";

import type {ServerPost} from "../types.ts";
import useShowDiscussion from "../util/use-show-discussion.ts";

import discussionApi, {type DiscussionApi} from "./api.ts";
import {
    clearAndFetchPostsAction,
    initializeDiscussionListComponentAction,
    updatePosts,
} from "./discussion-actions.ts";
import DiscussionList from "./discussion-list.tsx";
import {createDiscussionStore} from "./discussion-store.ts";

type DiscussionListProps = JSX.LibraryManagedAttributes<
    typeof DiscussionList,
    React.ComponentProps<typeof DiscussionList>
>;

// NOTE(jeff): Is this right? The exported DiscussionsList is already
// a connected component.
const ConnectedDiscussionList: React.ComponentType<DiscussionListProps> =
    connect()(DiscussionList);

type OwnProps = DiscussionListProps & {
    cursor: string;
    isComplete: boolean;
    apiOverrides?: DiscussionApi;
    // Initial set of posts to start with
    posts?: Array<ServerPost>;
    includeCheckboxes?: boolean;
};

type Props = OwnProps & WithApolloProps;

class StatefulDiscussionList extends React.Component<Props> {
    store: any;

    componentDidMount() {
        const {
            topicSlug,
            tab,
            posts,
            cursor,
            isComplete,
            apiOverrides,
            gqlClient,
        } = this.props;

        const store = this.getStore();
        const apiId = apiOverrides && apiOverrides.getId();

        if (apiOverrides && apiId) {
            discussionApi.register(apiId, apiOverrides);
        }

        store.dispatch(
            initializeDiscussionListComponentAction(
                topicSlug,
                tab,
                cursor,
                apiId,
                isComplete,
                posts,
                gqlClient,
            ),
        );
    }

    componentDidUpdate(prevProps: Props) {
        const {tab, posts, cursor, isComplete} = this.props;

        if (prevProps.posts !== posts) {
            const store = this.getStore();
            store.dispatch(updatePosts(tab, posts || [], cursor, isComplete));
        }
    }

    getStore(): any {
        if (!this.store) {
            this.store = createDiscussionStore(
                undefined,
                this.props.discussionContext,
            );
        }
        return this.store;
    }

    refresh() {
        this.getStore().dispatch(
            clearAndFetchPostsAction(this.props.gqlClient),
        );
    }

    render(): React.ReactNode {
        const {
            apiOverrides: _,
            cursor: __,
            isComplete: ___,
            posts: ____,
            gqlClient: _____,
            gqlDefaultModule: ______,
            ...restProps
        } = this.props;

        return (
            <Provider store={this.getStore()}>
                <ConnectedDiscussionList {...restProps} />
            </Provider>
        );
    }
}
type ExportProps = WithoutApollo<
    JSX.LibraryManagedAttributes<
        typeof StatefulDiscussionList,
        React.ComponentProps<typeof StatefulDiscussionList>
    >
>;

// NOTE(kevinb): We can't use HOCs for this component since some of its
// consumers make use of ref forwarding.  This is because almost all of
// our custom HOCs will not support ref forwarding in the future because
// there isn't a way to make the types work when wrapping a component in
// multiple HOCs.
export default React.forwardRef<StatefulDiscussionList, ExportProps>(
    (props, ref) => {
        const {gqlClient, gqlDefaultModule} = useApolloGqlClient();
        const shouldShowDiscussion = useShowDiscussion();

        if (!shouldShowDiscussion) {
            return null;
        }

        return (
            <StatefulDiscussionList
                {...props}
                gqlClient={gqlClient}
                gqlDefaultModule={gqlDefaultModule}
                ref={ref}
            />
        );
    },
);

export const StatefulDiscussionListNoGql = StatefulDiscussionList;
